export default class {

    constructor (component, validationProps) {
        this.#component = component;

        this.#validationPropsEnabled = validationProps.filter((prop) => Object.keys(this.#propToValidityMap).includes(prop));
        this.events = new wealthify.ComponentEvents(this.#component, this.constructor.eventSettings);

        this.#component.styleSelectors.add(this.#styleSelector);

        this.#component.deprecatedAttachAdditionalProps(this.#additionalProps);

        this.#addSelectorsToNodes();
        this.#setupAriaStatusRegion();

        this.#initialiseValidation();

        this.#component.addEventListener(this.#component.events.ValueChange.name, this.#updateStatusIfInteractedWith.bind(this));
        this.#component.addEventListener(this.events.StatusDismiss.name, this.#toggleInteractedWithAndUpdateStatus.bind(this));
        this.#component.addEventListener(this.events.StatusDismissAll.name, this.#dismissAllStatuses.bind(this));
        this.#component.addEventListener(this.events.ResetValidation.name, this.#resetValidation.bind(this));
        this.#component.addEventListener(this.events.FireValidation.name, this.#updateStatusIfInteractedWith.bind(this), true);
        this.#component.addEventListener(this.events.ToggleInteractedWith.name, () => this.#interactedWith = true);
    }

    // Public propeties

    get valid () {
        return Object.values(this.#errors).every((validationProp) => validationProp.passed === true);
    }

    get interactedWith () {
        return this.#interactedWith;
    }

    get passes () {
        const passes = {};

        for (const errorProperty in this.#errors) {
            const camelCaseName = wealthify.convert.toCamelCase(errorProperty);

            passes[camelCaseName] = this.#errors[errorProperty].passed;
        }

        return passes;
    }

    get message () {
        const statusesWithNoMessage = [
            this.#statuses.default.value,
            this.#statuses.success.value,
            this.#statuses.loading.value,
        ];

        if (statusesWithNoMessage.includes(this.#status)) {
            return '';
        }

        const relevantObject = this.valid ? this.#warnings : this.#errors;

        return Object.values(relevantObject).filter((propItem) => !propItem.passed)[0].message;
    }

    get breakingMessage () {
        const statusesWithNoMessage = [
            this.#statuses.success.value,
            this.#statuses.loading.value,
        ];

        if (statusesWithNoMessage.includes(this.#status)) {
            return '';
        }

        const failingProp = Object.values(this.#errors).filter((propItem) => !propItem.passed)[0];

        return failingProp ? failingProp.message : '';
    }

    static eventSettings = Object.freeze({events: {
        StatusDismiss: 'statusdismiss',
        StatusDismissAll: 'statusdismissall',
        ResetValidation: 'resetvalidation',
        FireValidation: 'firevalidation',
        ToggleInteractedWith: 'toggleinteractedwith',
        StatusChange: 'statuschange',
        MessageChange: 'messagechange',
        ValidationChanged: 'validationchanged', // mirrored in field.js, if changes, needs to change in both places.
        RequiredToggled: 'required-toggled'
    }});

    static validationPropEnums = Object.freeze({
        Custom: 'validate-custom',
        Required: 'validate-required',
        Pattern: 'validate-pattern',
        Max: 'validate-max',
        Min: 'validate-min',
        MaxLength: 'validate-maxlength',
        MinLength: 'validate-minlength',
        Step: 'validate-step',
    });

    static messagePropEnums = Object.freeze({
        Custom: 'message-custom',
        Required: 'message-required',
        Pattern: 'message-pattern',
        Max: 'message-max',
        Min: 'message-min',
        MaxLength: 'message-maxlength',
        MinLength: 'message-minlength',
        Step: 'message-step',
    });

    // Private propeties

    #styleSelector = 'c-field-validation-base';
    #component = null;
    #validationPropsEnabled = [];
    #interactedWith = false;

    #warningsProxy = {}; // Do not use directly, only use #warnings getter
    #errors = {};

    #statuses = Object.freeze({
        default: {
            value: 'default',
            icon: 'no-icon'
        },
        loading: {
            value: 'loading',
            icon: 'no-icon'
        },
        success: {
            value: 'success',
            icon: 'success'
        },
        warning: {
            value: 'warning',
            icon: 'warning'
        },
        error: {
            value: 'error',
            icon: 'danger'
        },
    })

    #propToValidityMap = Object.freeze({
        [this.constructor.validationPropEnums.Custom]: 'customError',
        [this.constructor.validationPropEnums.Required]: 'valueMissing',
        [this.constructor.validationPropEnums.Pattern]: 'patternMismatch',
        [this.constructor.validationPropEnums.Max]: 'rangeOverflow',
        [this.constructor.validationPropEnums.Min]: 'rangeUnderflow',
        [this.constructor.validationPropEnums.MaxLength]: 'tooLong',
        [this.constructor.validationPropEnums.MinLength]: 'tooShort',
        [this.constructor.validationPropEnums.Step]: 'stepMismatch',
    })

    #statusHistory = {
        current: this.#statuses.default.value,
        previous: null
    }

    get #fieldInput () {
        // Return first item if multiple inputs
        return Array.isArray(this.#component.dom.fieldinput) ? this.#component.dom.fieldinput[0] : this.#component.dom.fieldinput;
    }

    get #propToMessageMap () {
        const value = this.#component.value ?? this.#fieldInput.value;
        const textVariation = this.#component.canHaveMultipleInputs ? 'options selected' : 'characters';

        return Object.freeze({
            [this.constructor.validationPropEnums.Custom]: '',
            [this.constructor.validationPropEnums.Required]: 'This field is required.',
            [this.constructor.validationPropEnums.Pattern]: 'The format of this field is invalid.',
            [this.constructor.validationPropEnums.Max]: `This field must not exceed ${this.#component[this.constructor.validationPropEnums.Max]}.`,
            [this.constructor.validationPropEnums.Min]: `This field must be a minimum of ${this.#component[this.constructor.validationPropEnums.Min]}.`,
            [this.constructor.validationPropEnums.MaxLength]: `This field must not exceed ${this.#component[this.constructor.validationPropEnums.MaxLength]} ${textVariation}. Please shorten by ${value.length - this.#component[this.constructor.validationPropEnums.MaxLength]}.`,
            [this.constructor.validationPropEnums.MinLength]: `This field must have a minimum of ${this.#component[this.constructor.validationPropEnums.MinLength]} ${textVariation}. Please lengthen by ${this.#component[this.constructor.validationPropEnums.MinLength] - value.length}.`,
            [this.constructor.validationPropEnums.Step]: `This field must use step sizes of ${this.#component[this.constructor.validationPropEnums.Step]}. Please adjust the value to ${Math.floor(value / this.#component[this.constructor.validationPropEnums.Step]) * this.#component[this.constructor.validationPropEnums.Step]} or ${Math.floor(value / this.#component[this.constructor.validationPropEnums.Step] + 1) * this.#component[this.constructor.validationPropEnums.Step]}.`,
        });
    }

    #polyfilledValidationProps = Object.freeze({
        [this.constructor.validationPropEnums.Required]: ['radio', 'checkbox'],
        [this.constructor.validationPropEnums.Max]: ['text', 'select', 'radio', 'checkbox', 'textarea'],
        [this.constructor.validationPropEnums.Min]: ['text', 'select', 'radio', 'checkbox', 'textarea'],
        [this.constructor.validationPropEnums.MaxLength]: true,
        [this.constructor.validationPropEnums.MinLength]: true,
        [this.constructor.validationPropEnums.Pattern]: ['number', 'tel', 'date'],
    })

    #additionalProps = Object.freeze([
        {
            class: new wealthify.Prop(this.constructor.messagePropEnums.Custom),
            callback: () => {
                this.#fieldInput.setCustomValidity(this.#component[this.constructor.messagePropEnums.Custom]);
                // If prop is specified on component on page load
                // Then the below will not fire because the validation has not been initialised yet
                // This is rechecked and fired if required in #initialiseValidation
                this.#updateStatusIfInteractedWith();
            }
        },
        {
            class: new wealthify.Prop(this.constructor.messagePropEnums.Required),
            callback: () => this.events.MessageChange.dispatch()
        },
        {
            class: new wealthify.Prop(this.constructor.messagePropEnums.Pattern),
            callback: () => this.events.MessageChange.dispatch()
        },
        {
            class: new wealthify.Prop(this.constructor.messagePropEnums.Max),
            callback: () => this.events.MessageChange.dispatch()
        },
        {
            class: new wealthify.Prop(this.constructor.messagePropEnums.Min),
            callback: () => this.events.MessageChange.dispatch()
        },
        {
            class: new wealthify.Prop(this.constructor.messagePropEnums.Step),
            callback: () => this.events.MessageChange.dispatch()
        },
        {
            class: new wealthify.Prop(this.constructor.messagePropEnums.MaxLength),
            callback: () => this.events.MessageChange.dispatch()
        },
        {
            class: new wealthify.Prop(this.constructor.messagePropEnums.MinLength),
            callback: () => this.events.MessageChange.dispatch()
        },

        {
            class: new wealthify.Prop(this.constructor.validationPropEnums.Custom, {
                type: wealthify.propTypes.Number(),
                nullable: true
            }),
            callback: () => {}
        },
        {
            class: new wealthify.Prop(this.constructor.validationPropEnums.Required, {
                type: wealthify.propTypes.Boolean(),
                nullable: true
            }),
            callback: () => {
                this.#updateProp(this.constructor.validationPropEnums.Required);
                this.events.RequiredToggled.dispatch();
            }
        },
        {
            class: new wealthify.Prop(this.constructor.validationPropEnums.Pattern, {
                type: wealthify.propTypes.Regex(),
                nullable: true
            }),
            callback: () => {
                this.#updateProp(this.constructor.validationPropEnums.Pattern); 
                this.events.ValidationChanged.dispatch();
            }
        },
        {
            class: new wealthify.Prop(this.constructor.validationPropEnums.Max, {nullable: true}),
            callback: () => {
                this.#updateProp(this.constructor.validationPropEnums.Max);
                this.events.ValidationChanged.dispatch();
            }
        },
        {
            class: new wealthify.Prop(this.constructor.validationPropEnums.Min, {nullable: true}),
            callback: () => {
                this.#updateProp(this.constructor.validationPropEnums.Min);
                this.events.ValidationChanged.dispatch();
            }
        },
        {
            class: new wealthify.Prop(this.constructor.validationPropEnums.Step, {
                type: wealthify.propTypes.Number(),
                nullable: true
            }),
            callback: () => {
                this.#updateProp(this.constructor.validationPropEnums.Step);
                this.events.ValidationChanged.dispatch();
            }
        },
        {
            class: new wealthify.Prop(this.constructor.validationPropEnums.MaxLength, {
                type: wealthify.propTypes.Number(),
                nullable: true
            }),
            callback: () => {
                this.#updateProp(this.constructor.validationPropEnums.MaxLength);
                this.events.ValidationChanged.dispatch();
            }
        },
        {
            class: new wealthify.Prop(this.constructor.validationPropEnums.MinLength, {
                type: wealthify.propTypes.Number(),
                nullable: true
            }),
            callback: () => {
                this.#updateProp(this.constructor.validationPropEnums.MinLength);
                this.events.ValidationChanged.dispatch();
            }
        },
        {
            class: new wealthify.Prop('warnings', {
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.IsInArray(Object.keys(this.#propToValidityMap))],
                nullable: true
            }),
            callback: () => {
                this.#initialiseValidation();

                if (this.#status !== this.#statuses.default.value) {
                    this.#updateStatusIfInteractedWith();
                }
            }
        }
    ])

    get #hasUpcomingCorrection () {
        return (this.#status === this.#statuses.error.value || this.#status === this.#statuses.warning.value) && this.valid;
    }

    get #hasWarning () {
        const hasWarnings = Object.values(this.#warnings).some((validationProp) => validationProp.passed === false);

        return hasWarnings;
    }

    get #hasError () {
        return !this.valid;
    }

    get #hasSuccess () {
        return this.#status === this.#statuses.success.value && this.valid;
    }

    get #warnings () {
        this.#addAttributesToFieldInput(this.#warningsProxy);

        const warnings = wealthify.type.object.deepCopy(this.#warningsProxy);

        this.#removeAttributesFromFieldInput(this.#warningsProxy);

        return warnings;
    }

    set #warnings (value) {
        this.#warningsProxy = value;
    }

    get #status () {
        return this.#statusHistory.current;
    }

    set #status (value) {
        this.#statusHistory.previous = this.#status;
        this.#statusHistory.current = value;

        const hasUpdatedStatus = this.#status !== this.#previousStatus;

        if (hasUpdatedStatus) {
            this.#toggleStatusStyleModifier();
            this.#updateStatusIconAndAria();
        }
    }

    get #previousStatus () {
        return this.#statusHistory.previous;
    }

    #addSelectorsToNodes () {
        this.#component.dom.statusicon.classList.add(`${this.#styleSelector}__status-icon`);
    }

    #initialiseValidation () {
        this.#errors = {};
        this.#warnings = {};

        this.#validationPropsEnabled.forEach((prop) => {
            this.#configureValidationProp(prop);
            this.#removeAttributesFromFieldInput(this.#warnings);
            this.#addAttributesToFieldInput(this.#errors);
        });

        if (this.#component[this.constructor.messagePropEnums.Custom]) {
            // Required if custom error is specified on component on page load
            // Since the callback would've been fired before the validation initialisation
            this.#updateStatus();
        }
    }

    #setupAriaStatusRegion () {
        this.#component.dom.statusaria.classList.add('u-display__hidden');
        this.#component.dom.statusaria.setAttribute('aria-live', 'polite');
    }

    #configureValidationProp (prop) {
        const isWarning = this.#component.warnings !== undefined && this.#component.warnings.includes(prop);
        const globalThis = this;
        const propItem = {
            get globalThis () {
                return globalThis;
            },
            get passed () {
                return this.globalThis.#isPropPolyfilled(prop)
                    ? this.globalThis.#validationPolyfills[prop]()
                    : !this.globalThis.#fieldInput.validity[this.globalThis.#propToValidityMap[prop]];
            },
            get message () {
                const attributeName = prop.split('validate-')[1];
                const messageProp = this.globalThis.#component[`message-${attributeName}`];
                const isOverriddenMessage = messageProp !== '' && messageProp !== undefined;

                return isOverriddenMessage ? messageProp : this.globalThis.#propToMessageMap[prop];
            }
        };

        if (isWarning) {
            let warnings = {[prop]: propItem};

            Object.assign(warnings, this.#warningsProxy);

            this.#warnings = warnings;
        } else {
            this.#errors[prop] = propItem;
        }

    }

    #isPropPolyfilled (prop, thisContext = this) {
        const isGloballyPolyfilledProp = thisContext.#polyfilledValidationProps[prop] === true;
        const isPolyfilledPropForType = !isGloballyPolyfilledProp && Object.keys(thisContext.#polyfilledValidationProps).includes(prop) && thisContext.#polyfilledValidationProps[prop].includes(thisContext.#component.type);

        return isGloballyPolyfilledProp || isPolyfilledPropForType;
    }

    #addAttributesToFieldInput (validationProps) {
        Object.keys(validationProps).forEach((prop) => this.#toggleSingleAttributeOnFieldInput(prop));
    }

    #toggleSingleAttributeOnFieldInput (prop) {
        const attributeName = prop.split('validate-')[1];
        const isFalseyButNotZero = !this.#component[prop] && this.#component[prop] !== 0;

        if (isFalseyButNotZero) {
            this.#fieldInput.removeAttribute(attributeName);

            return;
        }

        this.#fieldInput.setAttribute(attributeName, this.#component[prop]);
    }

    #removeAttributesFromFieldInput (validationProps) {
        Object.keys(validationProps).forEach((prop) => {
            const attributeName = prop.split('validate-')[1];

            this.#fieldInput.removeAttribute(attributeName);
        });
    }

    #updateProp (prop) {
        const enabledPropsConfigured = this.#validationPropsEnabled !== undefined;

        if (!enabledPropsConfigured) {
            return;
        }

        const isEnabledProp = this.#validationPropsEnabled.includes(prop);

        if (!isEnabledProp) {
            return console.warn(`${this.#component.tagName} does not use the ${prop} prop.`);
        }

        this.#toggleSingleAttributeOnFieldInput(prop);

        this.#updateStatusIfInteractedWith();
    }

    #updateStatusIfInteractedWith () {
        if (this.#interactedWith) {
            this.#updateStatus();
        }
    }

    #updateStatus () {
        let status = this.#statuses.default.value;

        status = this.#hasSuccess || this.#hasUpcomingCorrection ? this.#statuses.success.value : status;
        status = this.#hasWarning ? this.#statuses.warning.value : status;
        status = this.#hasError ? this.#statuses.error.value : status;

        this.#status = status;
        this.events.StatusChange.dispatch();
    }

    #toggleInteractedWithAndUpdateStatus () {
        if (this.#interactedWith) {
            this.#dismissDismissableStatuses();
        } else {
            this.#updateStatus();
        }

        this.#interactedWith = true;
    }

    #dismissDismissableStatuses () {
        if (this.#hasSuccess) {
            this.#status = this.#statuses.default.value;
        }
    }

    #dismissAllStatuses () {
        this.#status = this.#statuses.default.value;
        this.events.StatusChange.dispatch();
    }

    #resetValidation () {
        this.#interactedWith = false;
        this.#dismissAllStatuses();
    }

    #toggleStatusStyleModifier () {
        Object.keys(this.#statuses).forEach((status) => {
            this.#component.styleSelectors.forEach((styleSelector) => this.#component.classList.remove(`${styleSelector}--${status}`));
        });

        this.#component.styleSelectors.forEach((styleSelector) => this.#component.classList.add(`${styleSelector}--${this.#status}`));
    }

    #updateStatusIconAndAria () {
        this.#component.dom.statusicon.type = this.#statuses[this.#status].icon;
        this.#component.dom.statusaria.innerHTML = this.valid ? 'valid' : 'invalid';
    }

    #validationPolyfills = {
        [this.constructor.validationPropEnums.Pattern]: () => {
            const isNonEmptyString = this.#component.value !== '' && this.#component.value !== undefined;

            if(isNonEmptyString){
                const regex = new RegExp(this.#component[this.constructor.validationPropEnums.Pattern]);

                return regex.test(this.#component.value);
            }

            return true;
        },
        [this.constructor.validationPropEnums.Max]: () => {
            const isNonEmptyString = this.#component.value !== '' && this.#component.value !== undefined;

            if (isNonEmptyString && this.#component[this.constructor.validationPropEnums.Max] !== undefined && this.#component[this.constructor.validationPropEnums.Max] !== null) {
                return Math.ceil(this.#component.value) <= this.#component[this.constructor.validationPropEnums.Max];
            }

            return true;
        },

        [this.constructor.validationPropEnums.Min]: () => {
            const isNonEmptyString = this.#component.value !== '' && this.#component.value !== undefined;

            if (isNonEmptyString && this.#component[this.constructor.validationPropEnums.Min] !== undefined && this.#component[this.constructor.validationPropEnums.Min] !== null) {
                return Math.floor(this.#component.value) >= this.#component[this.constructor.validationPropEnums.Min];
            }

            return true;
        },
        [this.constructor.validationPropEnums.MaxLength]: () => {
            if (this.#component[this.constructor.validationPropEnums.MaxLength] !== undefined && this.#component[this.constructor.validationPropEnums.MaxLength] !== null) {
                const length = this.#component.value ? this.#component.value.length : 0;

                return length <= this.#component[this.constructor.validationPropEnums.MaxLength];
            }

            return true;
        },
        [this.constructor.validationPropEnums.MinLength]: () => {
            const isNonEmptyString = this.#component.value !== '' && this.#component.value !== undefined;

            if (isNonEmptyString &&
                    this.#component[this.constructor.validationPropEnums.MinLength] !== undefined && 
                    this.#component[this.constructor.validationPropEnums.MinLength] !== null) {
                const length = this.#component.value ? this.#component.value.length : 0;

                return length >= this.#component[this.constructor.validationPropEnums.MinLength];
            }

            return true;
        },
        [this.constructor.validationPropEnums.Required]: () => {
            if (this.#component[this.constructor.validationPropEnums.Required] === true) {
                const isNonEmptyStringOrBoolean = (typeof this.#component.value === 'string' || typeof this.#component.value === 'boolean') &&
                    this.#component.value !== '' &&
                    this.#component.value !== undefined;
                const isNonEmptyArray = Array.isArray(this.#component.value) && this.#component.value.length > 0;

                return isNonEmptyStringOrBoolean || isNonEmptyArray;
            }

            return true;
        },
    };
}